import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
    Autocomplete
} from '@mui/material';
import useTextFields from '../../utility/hooks/useTextFields';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import Container from '@mui/material/Container';
import {useTheme} from '@mui/material/styles';
import {validateEmail, validateFieldLength, validatePhoneNumber} from '../../utility/validationUtil';
import {grey} from '@mui/material/colors';
import {useSubmitEnquiryFormMutation} from '../../api/services/formsService';
import useNotification from '../../utility/hooks/useNotification';
import {getSubdomain} from '../../utility/routeUtil';
import NotFoundPage from '../NotFound/NotFoundPage';
import EnquirySubmissionSuccess from './EnquirySubmissionSuccess';
import {useGetContactTitlesQuery, useGetSchoolsQuery, useGetRelationsQuery, useGetReligionsQuery} from '../../api/services/lookupsService';
import {useGetYearGroupsQuery, useGetBoarderGroupsQuery} from '../../api/services/groupService';
import AdmicityAddressLookup from '../../shared-components/AdmicityAddressLookup';

const getYearsOptions = () => {
    const currentYear = new Date().getFullYear();

    return Array.from({length: 8}, (_, i) => {
        const year = currentYear - 1 + i;

        return ({title: `${year}`, value: year});
    });
};

/* eslint-disable react/prop-types */
const initialFormFields = [
    {
        id: 'enquirer_title',
        name: 'enquirerTitleId',
        label: 'Title',
        inputType: 'select',
        options: [],
    },
    {
        id: 'enquirer_relationship',
        name: 'enquirerRelationship',
        label: 'Relationship',
        inputType: 'select',
        options: [],
    },
    {
        id: 'enquirer_forename',
        name: 'enquirerForename',
        label: 'Forename',
        inputType: 'text',
        validate: (value) => validateFieldLength(value, 60),
    },
    {
        id: 'enquirer_surname',
        name: 'enquirerSurname',
        label: 'Surname',
        inputType: 'text',
        validate: (value) => validateFieldLength(value, 60),
    },
    {
        id: 'enquirer_email',
        name: 'enquirerEmail',
        label: 'Email',
        inputType: 'text',
        validate: (value) => validateEmail(value)
    },
    {
        id: 'enquirer_mobile',
        name: 'enquirerMobile',
        label: 'Mobile Phone',
        inputType: 'text',
        type: 'tel',
        validate: validatePhoneNumber,
    },
    {
        id: 'pupil_forename',
        name: 'pupilForename',
        label: 'Forename',
        inputType: 'text',
        validate: (value) => validateFieldLength(value, 60),
    },
    {
        id: 'pupil_surname',
        name: 'pupilSurname',
        label: 'Surname',
        inputType: 'text',
        validate: (value) => validateFieldLength(value, 60),
    },
    {
        id: 'pupil_gender',
        name: 'pupilGender',
        label: 'Gender',
        inputType: 'select',
        options: [
            {title: 'Male', value: 'male'},
            {title: 'Female', value: 'female'}
        ],
    },
    {
        id: 'pupil_dateOfBirth',
        name: 'pupilDateOfBirth',
        label: 'Date of Birth',
        inputType: 'date',
        initialValue: null
    },
    {
        id: 'pupil_current_school',
        name: 'pupilCurrentSchool',
        label: 'Current School',
        inputType: 'select',
        required: false,
        options: [],
    },
    {
        id: 'pupil_year_entry',
        name: 'pupilYearEntry',
        label: 'Proposed Year of Entry',
        inputType: 'select',
        options: getYearsOptions(),
    },
    {
        id: 'pupil_season_entry',
        name: 'pupilSeasonEntry',
        label: 'Proposed Season of Entry',
        inputType: 'select',
        options: [
            {title: 'Autumn', value: 'Autumn'},
            {title: 'Spring', value: 'Spring'},
            {title: 'Summer', value: 'Summer'}
        ],
    },
    {
        id: 'pupil_year_group',
        name: 'pupilYearGroupId',
        label: 'Proposed Year Group',
        inputType: 'select',
        options: [],
    },
    {
        id: 'pupil_boarder_status',
        name: 'pupilBoarderStatus',
        label: 'Boarder Status',
        inputType: 'select',
        required: false,
        options: [],
    },
    {
        id: 'pupil_religion',
        name: 'pupilReligion',
        label: 'Religion',
        inputType: 'select',
        required: false,
        options: [],
    },
    {
        id: 'preferred_visit_date',
        name: 'preferredVisitDate',
        label: 'Preferred Visit Date',
        inputType: 'date',
    },
    {
        id: 'marketing_source',
        name: 'marketingSource',
        label: 'Marketing Source',
        inputType: 'select',
        options: [
            {title: 'Internet', value: 'Internet'},
            {title: 'Word of Mouth', value: 'Word of Mouth'},
            {title: 'Event', value: 'Event'},
            {title: 'Other', value: 'Other'},
        ],
    },
    {
        id: 'opt_in_updates',
        name: 'optInUpdates',
        label: 'Opt-in for updates',
        inputType: 'checkbox',
        required: false,
        initialValue: false
    },
];

const Field = ({field}) => {
    if (field == null) return null;

    const {options = []} = field.otherProps;
    const {id, name, label, required, value, onChange, error, helperText, type, inputType} = field.textFieldProps;

    const components = {
        select: name === 'pupilCurrentSchool'
            ? <Autocomplete
                freeSolo
                fullWidth
                value={value ? options.find(opt => opt.value === value) || { title: value, value } : null}
                onChange={(event, newValue) => {
                    const selectedValue = newValue
                        ? (typeof newValue === 'string' ? newValue : newValue.value)
                        : '';
                    onChange({
                        target: {
                            name,
                            value: selectedValue
                        }
                    });
                }}
                onInputChange={(event, newInputValue) => {
                    if (!event) return;
                    onChange({
                        target: {
                            name,
                            value: newInputValue
                        }
                    });
                }}
                options={options}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return option.title || '';
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        required={required}
                        error={error}
                        helperText={helperText}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props}>
                        {option.title}
                    </li>
                )}
            />
            : <FormControl fullWidth required={!!required} error={!!error}>
                <InputLabel id={`${id}-label`}>{label}</InputLabel>
                <Select
                    labelId={`${id}-label`}
                    id={id}
                    name={name}
                    label={label}
                    value={value}
                    onChange={onChange}
                >
                    {options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label || opt.title}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>,
        checkbox: <FormControlLabel
            required={required}
            control={
                <Checkbox
                    id={id}
                    name={name}
                    checked={Boolean(value)}
                    onChange={(e) => onChange({
                        target: {
                            name: e.target.name,
                            value: e.target.checked
                        }
                    })}
                />
            }
            label={label}
        />,
        date: <DatePicker
            format={'YYYY-MM-DD'}
            slotProps={{
                textField: {
                    sx: {
                        marginTop: 0,
                    },
                    fullWidth: true,
                    margin: 'dense',
                    name,
                    helperText,
                    required
                },
                field: {
                    readOnly: true
                }
            }}
            minDate={moment(new Date('1970-01-01'))}
            maxDate={moment()}
            label={label}
            value={value ? value : null}
            onChange={value => onChange({target: {value, name}}, {})}
        />,
        text: <TextField
            fullWidth
            required={!!required}
            error={!!error}
            helperText={helperText}
            id={id}
            name={name}
            label={label}
            value={value}
            onChange={onChange}
            type={type || 'text'}
        />
    };

    return components[inputType] || components.text;
};

const SectionHeader = ({children}) => {
    return <Typography
        variant="h6"
        fontWeight="bold"
        marginBottom={1}
        color={grey[900]}
    >
        {children}
    </Typography>;
};

const boxStyle = {
    my: 2,
    px: 3,
    py: 4,
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
};

const EnquiryForm = () => {
    const schoolSubdomain = getSubdomain();
    const isStagingOrEu = window.location.hostname.includes('.staging.admicity.co.uk') ||
        window.location.hostname.includes('.stg.admicity.co.uk') ||
        window.location.hostname.includes('.eu.admicity.co.uk') ||
        window.location.hostname.includes('localhost');

    if (!schoolSubdomain) {
        return <NotFoundPage/>;
    }

    const theme = useTheme();
    const {showErrorNotification, showDetailedErrorNotification} = useNotification();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [submitForm, {
        isLoading: isSubmitting,
        isSuccess: isSuccessfullySubmitted,
        isError,
        error
    }] = useSubmitEnquiryFormMutation();
    const {data: titles = [], error: titlesError} = useGetContactTitlesQuery();
    const {data: yearGroups = [], error: yearGroupsError} = useGetYearGroupsQuery();
    const {data: schools = [], error: schoolsError} = useGetSchoolsQuery();
    const {data: relations = [], error: relationsError} = useGetRelationsQuery();
    const {data: boarderGroups = [], error: boarderGroupsError} = useGetBoarderGroupsQuery();
    const {data: religions = [], error: religionsError} = useGetReligionsQuery();

    useEffect(() => {
        if (titlesError) {
            showErrorNotification('Failed to load contact titles');
        }
        if (yearGroupsError) {
            showErrorNotification('Failed to load year groups');
        }
        if (schoolsError) {
            showErrorNotification('Failed to load schools');
        }
        if (relationsError) {
            showErrorNotification('Failed to load relationships');
        }
        if (boarderGroupsError) {
            showErrorNotification('Failed to load boarder statuses');
        }
        if (religionsError) {
            showErrorNotification('Failed to load religions');
        }
    }, [titlesError, yearGroupsError, schoolsError, relationsError, boarderGroupsError, religionsError]);

    const formFields = useMemo(() => initialFormFields.map(field => {
        if (field.id === 'enquirer_title' && titles?.length) {
            return {
                ...field,
                options: titles?.map(title => ({
                    title: title.description,
                    value: title.id,
                }))
            };
        }

        if (field.id === 'enquirer_relationship' && relations?.length) {
            return {
                ...field,
                options: relations?.map(relation => ({
                    title: relation.description,
                    value: relation.description,
                }))
            };
        }

        if (field.id === 'pupil_year_group' && yearGroups?.length) {
            return {
                ...field,
                options: yearGroups
                    ?.map(group => ({
                        title: group.name,
                        value: group.id,
                    }))
                    .sort((a, b) => {
                        const matchA = a.title.match(/\d+/);
                        const matchB = b.title.match(/\d+/);

                        if (matchA && matchB && a.title.startsWith('Year ') && b.title.startsWith('Year ')) {
                            const numA = parseInt(matchA[0], 10);
                            const numB = parseInt(matchB[0], 10);
                            return numA - numB;
                        }

                        return a.title.localeCompare(b.title);
                    })
            };
        }

        if (field.id === 'pupil_current_school' && schools?.length) {
            if (isStagingOrEu) {
                return null;
            }

            const uniqueSchools = [...new Set(schools.map(school => school.name))];

            return {
                ...field,
                options: uniqueSchools.map(name => ({
                    title: name,
                    value: name
                }))
            };
        }

        if (field.id === 'pupil_boarder_status') {
            if (!boarderGroups?.length) {
                return null;
            }
            return {
                ...field,
                options: boarderGroups?.map(group => ({
                    title: group.name,
                    value: group.shortName
                }))
            };
        }

        if (field.id === 'pupil_religion') {
            if (!isStagingOrEu || !religions?.length) {
                return null;
            }
            return {
                ...field,
                options: religions?.map(religion => ({
                    title: religion.description,
                    value: religion.shortName,
                }))
            };
        }

        if (field.id === 'preferred_visit_date') {
            return isStagingOrEu ? null : field;
        }

        return field;
    }).filter(Boolean), [titles.length, yearGroups.length, schools.length, relations.length, boarderGroups.length, religions.length, isStagingOrEu]);

    const {
        fields,
        getFieldValues
    } = useTextFields(formFields);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const isFormValid = Object.values(fields).every(({textFieldProps: props}) =>
        !props.error && (!props.required || (props.value !== '' && props.value !== null))
    ) && (
       selectedAddress?.town && selectedAddress?.postcode
    );

    const handleAddressSelect = (address) => {
        setSelectedAddress(address);
    };

    useEffect(() => {
        if (isError) {
            if (error?.data?.validationErrors) {
                showDetailedErrorNotification('Failed to submit form', error.data.validationErrors);
            } else {
                showErrorNotification('Failed to submit form');
            }
        }
    }, [isError, error]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const body = {...getFieldValues()};
        const dateFields = initialFormFields
            .filter(field => field.inputType === 'date')
            .map(field => field.name);

        dateFields.forEach(dateField => {
            body[dateField] = moment.isMoment(body[dateField])
                ? body[dateField]?.format('YYYY-MM-DD')
                : body[dateField];
        });

        body.apartment = selectedAddress.apartment;
        body.houseName = selectedAddress.houseName;
        body.houseNumber = selectedAddress.houseNumber;
        body.street = selectedAddress.street;
        body.town = selectedAddress.town;
        body.postcode = selectedAddress.postcode;
        body.countryCode = selectedAddress.countryCode;
        body.county = selectedAddress.county;
        body.district = selectedAddress.district;

        await submitForm(body);
    };

    if (isSuccessfullySubmitted) {
        return <EnquirySubmissionSuccess/>;
    }

    return (
        <Container maxWidth="md">
            <Box
                sx={{mx: {xs: 1, sm: 0}}}
                component="form"
                onSubmit={handleSubmit}
            >
                <Box sx={boxStyle}>
                <Typography
                    variant="h4"
                    textAlign="center"
                    fontWeight="bold"
                    color={grey[900]}
                    py={{ xs: 3, sm: 6 }}
                >Enquiry Form</Typography>
                </Box>
                <Box sx={boxStyle}>
                <SectionHeader>Enquirer Details</SectionHeader>
                <Grid container spacing={2} marginBottom={3}>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerTitleId}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerForename}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerSurname}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerRelationship}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerEmail}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.enquirerMobile}/>
                    </Grid>
                </Grid>
            </Box>
                <Box sx={boxStyle}>
                <SectionHeader>Prospective Pupil Details</SectionHeader>
                <Grid container spacing={2} marginBottom={3}>
                    <Grid item xs={12} md={6}>
                        <Field field={fields.pupilForename}/>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Field field={fields.pupilSurname}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.pupilGender}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.pupilDateOfBirth}/>
                    </Grid>
                    {fields.pupilBoarderStatus && (
                        <Grid item xs={12} md={4}>
                            <Field field={fields.pupilBoarderStatus}/>
                        </Grid>
                    )}
                    {!isStagingOrEu && (
                        <Grid item xs={12} md={4}>
                            <Field field={fields.pupilCurrentSchool}/>
                        </Grid>
                    )}
                    {fields.pupilReligion && (
                        <Grid item xs={12} md={4}>
                            <Field field={fields.pupilReligion}/>
                        </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                        <Field field={fields.pupilYearEntry}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.pupilSeasonEntry}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.pupilYearGroupId}/>
                    </Grid>
                </Grid>
                </Box>
                <Box sx={boxStyle}>
                <SectionHeader>Visit & Marketing Preferences</SectionHeader>
                <Grid container spacing={2} marginBottom={3}>
                    {fields.preferredVisitDate && (
                        <Grid item xs={12} md={4}>
                            <Field field={fields.preferredVisitDate}/>
                        </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                        <Field field={fields.marketingSource}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Field field={fields.optInUpdates}/>
                    </Grid>
                </Grid>
                <Grid container spacing={1} marginBottom={3}>
                    <Grid item xs={12} md={12}>
                        <AdmicityAddressLookup onAddressSelect={handleAddressSelect} defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}></AdmicityAddressLookup>
                    </Grid>
                </Grid>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        my: 2,
                    }}
                >
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth={isSmallScreen}
                    disabled={isSubmitting || !isFormValid}
                >
                    Submit
                </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default EnquiryForm;
